import { useState, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdAdd, MdDelete, MdToggleOn, MdToggleOff, MdClose, MdVisibility, MdImage, MdVideocam, MdOpenInNew } from 'react-icons/md';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';

const getCurrentDateTimeLocal = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
};

export default function Ads() {
  const [ads, setAds] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [previewModal, setPreviewModal] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const { page, limit, setPage, setLimit } = usePagination(20);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [form, setForm] = useState({
    ad_type: 'sponsored_card', title: '', summary: '', image_url: '', video_url: '', media_type: 'image',
    link_url: '', region: 'all_india', is_active: false,
    starts_at: '', ends_at: '', impression_cap: '', media: [],
  });

  const fetchAds = async (fetchPage = page, fetchLimit = limit, shouldScroll = false) => {
    if (shouldScroll) {
      window.scrollTo(0, 0);
    }
    try {
      const res = await api.get('/admin/ads', { params: { page: fetchPage, limit: fetchLimit } });
      setAds(res.data.data);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      toast.error('Failed to load ads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAds(page, limit);
    api.get('/cities').then(res => setCities(res.data.data)).catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const set = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    setFormErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const uploadMediaFile = async (file, forcedType) => {
    if (!file) return;

    const isVideo = forcedType === 'video' || file.type.startsWith('video') || file.name.toLowerCase().endsWith('.mp4');

    if (isVideo) {
      const isMp4 = file.type === 'video/mp4' || file.name.toLowerCase().endsWith('.mp4');
      if (!isMp4) {
        toast.error('Only MP4 video format is allowed for video ads');
        return;
      }
      if (file.size > 100 * 1024 * 1024) {
        toast.error('Video size exceeds 100MB limit');
        return;
      }
    } else {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size exceeds 5MB limit');
        return;
      }
    }

    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);

    try {
      const endpoint = isVideo ? '/admin/upload/ad-video' : '/admin/upload/ad-image';
      const res = await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const newMedia = {
        url: res.data.data.url,
        type: isVideo ? 'video' : 'image',
        thumbnail_url: res.data.data.thumbnail_url || null,
      };

      const updatedMedia = [...(form.media || []), newMedia];
      set('media', updatedMedia);

      if (isVideo) {
        set('video_url', res.data.data.url);
        set('media_type', 'video');
        if (!form.image_url) {
          set('image_url', res.data.data.thumbnail_url || res.data.data.url);
        }
        toast.success('MP4 Video uploaded');
      } else {
        if (!form.image_url) {
          set('image_url', res.data.data.url);
        }
        toast.success('Image uploaded');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const validateForm = () => {
    const errs = {};
    const hasMedia = (form.media && form.media.length > 0) || (form.image_url && form.image_url.trim()) || (form.video_url && form.video_url.trim());
    if (!hasMedia) {
      errs.image_url = 'At least one Ad image or MP4 video is required';
    }
    const now = new Date();
    const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

    if (form.starts_at && new Date(form.starts_at) < fiveMinutesAgo) {
      errs.starts_at = 'Start date and time cannot be in the past';
    }
    if (form.ends_at && new Date(form.ends_at) < now) {
      errs.ends_at = 'End date and time cannot be in the past';
    }
    if (form.starts_at && form.ends_at && new Date(form.ends_at) <= new Date(form.starts_at)) {
      errs.ends_at = 'End date must be after start date';
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      if (formErrors.image_url) toast.error(formErrors.image_url);
      else if (formErrors.starts_at) toast.error(formErrors.starts_at);
      else if (formErrors.ends_at) toast.error(formErrors.ends_at);
      return;
    }
    try {
      const payload = { ...form };
      if (!payload.starts_at) delete payload.starts_at;
      if (!payload.ends_at) delete payload.ends_at;
      if (!payload.impression_cap) delete payload.impression_cap;
      else payload.impression_cap = parseInt(payload.impression_cap);

      if (payload.media && payload.media.length > 0) {
        const videoMedia = payload.media.find(m => m.type === 'video' || (m.url && m.url.includes('.mp4')));
        const imageMedia = payload.media.find(m => (m.type === 'image' || !m.type) && (!m.url || !m.url.includes('.mp4')));

        if (videoMedia) {
          payload.video_url = videoMedia.url;
        }
        if (imageMedia) {
          payload.image_url = imageMedia.url;
        } else if (videoMedia && !payload.image_url) {
          payload.image_url = videoMedia.thumbnail_url || videoMedia.url;
        }

        payload.media_type = videoMedia ? 'video' : 'image';
      }

      await api.post('/admin/ads', payload);
      toast.success('Ad created');
      setShowForm(false);
      setForm({
        ad_type: 'sponsored_card', title: '', summary: '', image_url: '', video_url: '', media_type: 'image',
        link_url: '', region: 'all_india', is_active: false, starts_at: '', ends_at: '', impression_cap: '', media: []
      });
      setFormErrors({});
      setPage(1);
      fetchAds(1, limit);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const toggleAd = async (id) => {
    try {
      await api.patch(`/admin/ads/${id}/toggle`);
      fetchAds(page, limit, false);
    } catch (err) {
      toast.error('Toggle failed');
    }
  };

  const confirmDeleteAd = async () => {
    if (!deleteModal) return;
    const deletedId = deleteModal.id;
    setDeleting(true);
    try {
      await api.delete(`/admin/ads/${deletedId}`);
      toast.success('Ad deleted');
      setDeleteModal(null);
      setAds(prev => prev.filter(a => a.id !== deletedId));
      fetchAds(page, limit, false);
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const getCtr = (ad) => ad.impression_count > 0
    ? ((ad.tap_count / ad.impression_count) * 100).toFixed(2) + '%'
    : '0%';

  const getStatus = (ad) => {
    if (!ad.is_active) return 'Inactive';
    if (ad.impression_cap && ad.impression_count >= ad.impression_cap) return 'Capped';
    if (ad.ends_at && new Date(ad.ends_at) < new Date()) return 'Ended';
    if (ad.starts_at && new Date(ad.starts_at) > new Date()) return 'Scheduled';
    return 'Active';
  };

  const isVideoAd = (ad) => {
    return ad.media_type === 'video' ||
      (ad.video_url && ad.video_url.trim().length > 0) ||
      (ad.media && ad.media.some(m => m.media_type === 'video' || m.type === 'video' || (m.media_url && m.media_url.includes('.mp4'))));
  };

  const getAdVideoUrl = (ad) => {
    if (ad.video_url) return ad.video_url;
    const videoMedia = ad.media?.find(m => m.media_type === 'video' || m.type === 'video');
    return videoMedia ? (videoMedia.media_url || videoMedia.url) : null;
  };

  const getAdImageUrl = (ad) => {
    if (ad.image_url) return ad.image_url;
    const imgMedia = ad.media?.find(m => m.media_type === 'image' || m.type === 'image');
    return imgMedia ? (imgMedia.media_url || imgMedia.url) : null;
  };

  const getAdMediaList = (ad) => {
    if (!ad) return [];
    const list = [];
    const seenUrls = new Set();

    if (Array.isArray(ad.media) && ad.media.length > 0) {
      ad.media.forEach((m, idx) => {
        const url = m.media_url || m.url;
        if (url && !seenUrls.has(url)) {
          seenUrls.add(url);
          const isVid = m.media_type === 'video' || m.type === 'video' || url.toLowerCase().includes('.mp4');
          list.push({
            id: m.id || idx,
            url,
            type: isVid ? 'video' : 'image',
            thumbnail_url: m.thumbnail_url || null,
          });
        }
      });
    }

    if (ad.video_url && !seenUrls.has(ad.video_url)) {
      seenUrls.add(ad.video_url);
      list.push({
        id: 'ad_video',
        url: ad.video_url,
        type: 'video',
        thumbnail_url: ad.image_url || null,
      });
    }

    if (ad.image_url && !seenUrls.has(ad.image_url) && ad.image_url !== ad.video_url) {
      seenUrls.add(ad.image_url);
      list.push({
        id: 'ad_image',
        url: ad.image_url,
        type: 'image',
        thumbnail_url: null,
      });
    }

    return list;
  };

  const openPreview = (ad) => {
    setPreviewModal(ad);
    setPreviewIndex(0);
  };

  if (loading) return <div className="page-loader">Loading...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h2>Ads Manager</h2>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? <><MdClose /> Cancel</> : <><MdAdd /> Create Ad</>}
        </button>
      </div>

      {showForm && (
        <form className="card form-card" onSubmit={handleCreate}>
          <div className="form-row">
            <div className="form-group">
              <label>Ad Type</label>
              <div className="radio-group">
                <label className={`radio-card ${form.ad_type === 'sponsored_card' ? 'selected' : ''}`}>
                  <input type="radio" value="sponsored_card" checked={form.ad_type === 'sponsored_card'}
                    onChange={() => set('ad_type', 'sponsored_card')} /> Sponsored Card
                </label>
                <label className={`radio-card ${form.ad_type === 'fullscreen' ? 'selected' : ''}`}>
                  <input type="radio" value="fullscreen" checked={form.ad_type === 'fullscreen'}
                    onChange={() => set('ad_type', 'fullscreen')} /> Full Screen
                </label>
              </div>
            </div>
          </div>

          {form.ad_type === 'sponsored_card' && (
            <>
              <div className="form-group">
                <label>Title</label>
                <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Ad title" />
              </div>
              <div className="form-group">
                <label>Summary</label>
                <textarea value={form.summary} onChange={e => set('summary', e.target.value)} placeholder="Ad summary" rows={3} />
              </div>
            </>
          )}

          <div className="form-group border-t border-gray-200 pt-4 mt-4">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
              <label style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>
                Ad Media Gallery (Images & MP4 Videos)
              </label>
              <span style={{ fontSize: 11, background: '#EFF6FF', color: '#1D4ED8', padding: '3px 8px', borderRadius: 12, fontWeight: 500, border: '1px solid #DBEAFE' }}>
                Supports JPG, PNG, WEBP & MP4 Video (up to 100MB)
              </span>
            </div>

            {/* Upload action buttons */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-outline"
                disabled={uploading}
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.onchange = (e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadMediaFile(file, 'image');
                  };
                  input.click();
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}
              >
                <MdImage size={18} /> Upload Image
              </button>

              <button
                type="button"
                className="btn btn-outline"
                disabled={uploading}
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'video/mp4,video/*';
                  input.onchange = (e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadMediaFile(file, 'video');
                  };
                  input.click();
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', fontSize: 13, borderColor: '#6366F1', color: '#4F46E5', cursor: 'pointer' }}
              >
                <MdVideocam size={18} /> Upload MP4 Video
              </button>

              {uploading && (
                <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 13, color: '#6366F1', gap: 6 }}>
                  Uploading media...
                </span>
              )}
            </div>

            {/* Media items thumbnails gallery */}
            <div className="flex-row gap-4 flex-wrap mb-sm" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {(form.media || []).map((m, i) => (
                <div key={i} style={{ position: 'relative', width: 90, height: 90, borderRadius: 8, overflow: 'hidden', border: '1px solid #ddd', background: '#000' }}>
                  {m.type === 'video' || (m.url && m.url.includes('.mp4')) ? (
                    <>
                      <video src={m.url} poster={m.thumbnail_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <span style={{ position: 'absolute', bottom: 4, left: 4, background: 'rgba(0,0,0,0.8)', color: '#fff', fontSize: 10, padding: '1px 5px', borderRadius: 4, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                        <MdVideocam size={12} /> MP4
                      </span>
                    </>
                  ) : (
                    <>
                      <img src={m.url} alt={`ad-media-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <span style={{ position: 'absolute', bottom: 4, left: 4, background: 'rgba(0,0,0,0.8)', color: '#fff', fontSize: 10, padding: '1px 5px', borderRadius: 4, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                        <MdImage size={12} /> IMG
                      </span>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      const remaining = form.media.filter((_, index) => index !== i);
                      set('media', remaining);
                      if (remaining.length === 0) {
                        set('image_url', '');
                        set('video_url', '');
                        set('media_type', 'image');
                      } else if (remaining[0].type === 'video') {
                        set('media_type', 'video');
                        set('video_url', remaining[0].url);
                        set('image_url', remaining[0].thumbnail_url || remaining[0].url);
                      } else {
                        set('media_type', 'image');
                        set('video_url', '');
                        set('image_url', remaining[0].url);
                      }
                    }}
                    style={{
                      position: 'absolute', top: 3, right: 3, background: 'rgba(239, 35, 60, 0.9)', color: 'white',
                      borderRadius: '50%', width: 20, height: 20, border: 'none', cursor: 'pointer',
                      fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold'
                    }}
                    title="Remove media"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <p className="text-muted text-xs">
              {form.ad_type === 'fullscreen'
                ? 'For Full Screen Ads, upload an Image or an MP4 Video as the primary media.'
                : 'Upload images and MP4 videos to create a carousel for this ad.'}
            </p>
            {formErrors.image_url && <span className="field-error">{formErrors.image_url}</span>}
          </div>

          <div className="form-group">
            <label>Link URL (optional)</label>
            <input value={form.link_url} onChange={e => set('link_url', e.target.value)} placeholder="https://..." />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <select value={form.region} onChange={e => set('region', e.target.value)}>
                <option value="all_india">All Cities</option>
                {cities.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Impression Cap</label>
              <input type="number" value={form.impression_cap} onChange={e => set('impression_cap', e.target.value)} placeholder="Unlimited" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="datetime-local"
                min={getCurrentDateTimeLocal()}
                value={form.starts_at}
                onChange={e => set('starts_at', e.target.value)}
              />
              {formErrors.starts_at && <span className="field-error">{formErrors.starts_at}</span>}
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="datetime-local"
                min={form.starts_at || getCurrentDateTimeLocal()}
                value={form.ends_at}
                onChange={e => set('ends_at', e.target.value)}
              />
              {formErrors.ends_at && <span className="field-error">{formErrors.ends_at}</span>}
            </div>
          </div>

          <label className="checkbox-label">
            <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
            Active immediately
          </label>

          <button type="submit" className="btn btn-primary mt-md">Create Ad</button>
        </form>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Type</th>
              <th className="hide-mobile">Region</th>
              <th>Status</th>
              <th className="hide-mobile">Impressions</th>
              <th className="hide-mobile">Taps</th>
              <th className="hide-mobile">CTR</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {ads.map(ad => {
              const mediaList = getAdMediaList(ad);
              const hasVideo = mediaList.some(m => m.type === 'video');
              const hasImage = mediaList.some(m => m.type === 'image');
              return (
                <tr key={ad.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span className="badge">{ad.ad_type === 'sponsored_card' ? 'Sponsored' : 'Fullscreen'}</span>
                      {hasVideo && (
                        <span className="badge" style={{
                          fontSize: 11,
                          background: '#F3E8FF',
                          color: '#7E22CE',
                          border: '1px solid #E9D5FF'
                        }}>
                          🎬 Video
                        </span>
                      )}
                      {hasImage && (
                        <span className="badge" style={{
                          fontSize: 11,
                          background: '#F0FDF4',
                          color: '#15803D',
                          border: '1px solid #DCFCE7'
                        }}>
                          🖼️ Image
                        </span>
                      )}
                    </div>
                    {ad.title && <p className="cell-sub">{ad.title}</p>}
                  </td>
                  <td className="hide-mobile">{ad.region?.replace('_', ' ')}</td>
                  <td>
                    <span className={`badge ${getStatus(ad) === 'Active' ? 'badge-live' : getStatus(ad) === 'Inactive' ? 'badge-expired' : ''}`}>
                      {getStatus(ad)}
                    </span>
                  </td>
                  <td className="hide-mobile">{ad.impression_count}{ad.impression_cap ? `/${ad.impression_cap}` : ''}</td>
                  <td className="hide-mobile">{ad.tap_count}</td>
                  <td className="hide-mobile">{getCtr(ad)}</td>
                  <td>
                    <div className="action-btns">
                      <button className="btn btn-sm btn-ghost" title="Preview Ad" onClick={() => openPreview(ad)}>
                        <MdVisibility />
                      </button>
                      <button className="btn btn-sm btn-ghost" title={ad.is_active ? 'Deactivate' : 'Activate'} onClick={() => toggleAd(ad.id)}>
                        {ad.is_active ? <MdToggleOn className="text-green" /> : <MdToggleOff />}
                      </button>
                      <button className="btn btn-sm btn-danger" title="Delete Ad" onClick={() => setDeleteModal(ad)}>
                        <MdDelete />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {ads.length === 0 && <tr><td colSpan="7" className="empty-row">No ads yet</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="mobile-cards">
        {ads.map(ad => {
          const mediaList = getAdMediaList(ad);
          const hasVideo = mediaList.some(m => m.type === 'video');
          const hasImage = mediaList.some(m => m.type === 'image');
          return (
            <div key={ad.id} className="mobile-card">
              <div className="mobile-card-header">
                <span className={`badge ${getStatus(ad) === 'Active' ? 'badge-live' : 'badge-expired'}`}>{getStatus(ad)}</span>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <span className="badge">{ad.ad_type === 'sponsored_card' ? 'Sponsored' : 'Fullscreen'}</span>
                  {hasVideo && (
                    <span className="badge" style={{
                      fontSize: 11,
                      background: '#F3E8FF',
                      color: '#7E22CE'
                    }}>
                      🎬 Video
                    </span>
                  )}
                  {hasImage && (
                    <span className="badge" style={{
                      fontSize: 11,
                      background: '#F0FDF4',
                      color: '#15803D'
                    }}>
                      🖼️ Image
                    </span>
                  )}
                </div>
              </div>
              {ad.title && <h4>{ad.title}</h4>}
              <div className="mobile-card-stats">
                <span>{ad.impression_count} impr</span>
                <span>{ad.tap_count} taps</span>
                <span>CTR: {getCtr(ad)}</span>
              </div>
              <div className="action-btns">
                <button className="btn btn-sm btn-ghost" onClick={() => openPreview(ad)}>
                  <MdVisibility /> Preview
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => toggleAd(ad.id)}>
                  {ad.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(ad)}><MdDelete /> Delete</button>
              </div>
            </div>
          );
        })}
      </div>

      {ads.length > 0 && (
        <Pagination
          pagination={pagination}
          onPageChange={(p) => {
            setPage(p);
            window.scrollTo(0, 0);
          }}
          limit={limit}
          onLimitChange={setLimit}
        />
      )}

      {/* Full-Screen Ad Preview Modal */}
      {previewModal && (() => {
        const mediaList = getAdMediaList(previewModal);
        const activeMedia = mediaList[previewIndex] || mediaList[0] || {
          url: getAdVideoUrl(previewModal) || getAdImageUrl(previewModal),
          type: isVideoAd(previewModal) ? 'video' : 'image'
        };

        return (
          <div className="modal-overlay" onClick={() => setPreviewModal(null)}>
            <div className="modal" style={{ maxWidth: 460, padding: 0, overflow: 'hidden', borderRadius: 20, background: '#111827', color: '#fff', animation: 'none' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>
                    {previewModal.ad_type === 'fullscreen' ? 'Full-Screen Ad Preview' : 'Sponsored Card Preview'}
                  </span>
                  {mediaList.some(m => m.type === 'video') && (
                    <span className="badge" style={{ fontSize: 11, background: '#7E22CE', color: '#fff' }}>
                      🎬 Video
                    </span>
                  )}
                  {mediaList.some(m => m.type === 'image') && (
                    <span className="badge" style={{ fontSize: 11, background: '#047857', color: '#fff' }}>
                      🖼️ Image
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setPreviewModal(null)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.12)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 8,
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffffff',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)'; }}
                  title="Close"
                  aria-label="Close"
                >
                  <MdClose size={18} />
                </button>
              </div>

              <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {/* Media Switcher when multiple media exist */}
                {mediaList.length > 1 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    marginBottom: 14,
                    width: '100%',
                    background: 'rgba(255,255,255,0.06)',
                    padding: '8px 12px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <span style={{ fontSize: 12, color: '#9CA3AF', marginRight: 4, fontWeight: 500 }}>View Media:</span>
                    {mediaList.map((item, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setPreviewIndex(idx)}
                        style={{
                          padding: '6px 14px',
                          borderRadius: 8,
                          fontSize: 12.5,
                          fontWeight: 700,
                          border: previewIndex === idx ? '1.5px solid #60A5FA' : '1px solid rgba(255,255,255,0.15)',
                          background: previewIndex === idx ? '#2563EB' : 'rgba(255,255,255,0.08)',
                          color: '#fff',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          transition: 'all 0.15s ease',
                          boxShadow: previewIndex === idx ? '0 2px 8px rgba(37, 99, 235, 0.4)' : 'none'
                        }}
                      >
                        {item.type === 'video' ? <MdVideocam size={15} /> : <MdImage size={15} />}
                        {item.type === 'video' ? 'Video' : 'Image'} {mediaList.length > 2 ? `#${idx + 1}` : ''}
                      </button>
                    ))}
                  </div>
                )}

                {/* Main Media Player / Image Display */}
                <div style={{
                  width: '100%',
                  height: 460,
                  minHeight: 460,
                  maxHeight: 520,
                  borderRadius: 16,
                  overflow: 'hidden',
                  background: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}>
                  {activeMedia.type === 'video' ? (
                    <video
                      key={activeMedia.url}
                      src={activeMedia.url}
                      controls
                      autoPlay
                      loop
                      playsInline
                      style={{ width: '100%', height: '100%', maxHeight: 520, objectFit: 'contain' }}
                    />
                  ) : (
                    <img
                      key={activeMedia.url}
                      src={activeMedia.url}
                      alt={previewModal.title || 'Ad Image'}
                      style={{ width: '100%', height: '100%', maxHeight: 520, objectFit: 'contain' }}
                    />
                  )}

                  {mediaList.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewIndex(prev => (prev > 0 ? prev - 1 : mediaList.length - 1));
                        }}
                        style={{
                          position: 'absolute',
                          left: 10,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0,0,0,0.65)',
                          color: '#fff',
                          border: '1px solid rgba(255,255,255,0.3)',
                          borderRadius: '50%',
                          width: 34,
                          height: 34,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: 18,
                          fontWeight: 'bold',
                          zIndex: 3
                        }}
                        title="Previous Media"
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewIndex(prev => (prev < mediaList.length - 1 ? prev + 1 : 0));
                        }}
                        style={{
                          position: 'absolute',
                          right: 10,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'rgba(0,0,0,0.65)',
                          color: '#fff',
                          border: '1px solid rgba(255,255,255,0.3)',
                          borderRadius: '50%',
                          width: 34,
                          height: 34,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: 18,
                          fontWeight: 'bold',
                          zIndex: 3
                        }}
                        title="Next Media"
                      >
                        ›
                      </button>
                      <span style={{
                        position: 'absolute',
                        bottom: 10,
                        right: 12,
                        background: 'rgba(0,0,0,0.75)',
                        color: '#fff',
                        fontSize: 11,
                        padding: '3px 8px',
                        borderRadius: 12,
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.15)',
                        zIndex: 2
                      }}>
                        {previewIndex + 1} / {mediaList.length} ({activeMedia.type === 'video' ? '🎬 Video' : '🖼️ Image'})
                      </span>
                    </>
                  )}
                </div>

                {previewModal.title && (
                  <h4 style={{ margin: '14px 0 4px', fontSize: 16, textAlign: 'center', color: '#fff' }}>{previewModal.title}</h4>
                )}
                {previewModal.summary && (
                  <p style={{ fontSize: 13, color: '#9CA3AF', margin: '0 0 10px', textAlign: 'center' }}>{previewModal.summary}</p>
                )}

                {previewModal.link_url && (
                  <a
                    href={previewModal.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{ width: '100%', marginTop: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 10 }}
                  >
                    <MdOpenInNew size={16} /> Visit Ad Link
                  </a>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: 12, fontSize: 12, color: '#9CA3AF', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 10 }}>
                  <span>Region: {previewModal.region?.replace('_', ' ')}</span>
                  <span>Impressions: {previewModal.impression_count}</span>
                  <span>Status: {getStatus(previewModal)}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" style={{ animation: 'none' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Ad</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this ad?</p>
              {deleteModal.title && <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.title}"</p>}
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDeleteAd} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
